import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import SEO from "@/components/SEO";
import AirtableFormEmbed from "@/components/AirtableFormEmbed";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FORMS } from "@/constants/brand";
import { useTranslation } from "@/i18n/LanguageContext";

export default function WaitlistPage() {
    const { hash } = useLocation();
    const [tab, setTab] = useState("homeowner");
    const { t } = useTranslation();

    useEffect(() => {
        if (hash === "#provider") setTab("provider");
        else setTab("homeowner");
    }, [hash]);

    return (
        <div data-testid="waitlist-page">
            <SEO
                title={t("waitlist.seo.title")}
                description={t("waitlist.seo.description")}
                keywords="join community, women-owned home services, become a founding provider, home services directory"
                path="/waitlist"
            />

            <section className="bg-cream">
                <div className="container-x pt-20 lg:pt-28 pb-12 max-w-4xl">
                    <p className="label-eyebrow">{t("waitlist.hero.eyebrow")}</p>
                    <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-heading tracking-tight leading-[1.05] text-ink">
                        {t("waitlist.hero.title1")}{" "}
                        <span className="italic text-clay">{t("waitlist.hero.titleAccent")}</span>
                    </h1>
                    <p className="mt-6 text-lg text-ink-soft leading-relaxed max-w-2xl">
                        {t("waitlist.hero.body")}
                    </p>
                </div>
            </section>

            <section className="bg-cream pb-24">
                <div className="container-x">
                    <Tabs value={tab} onValueChange={setTab} className="w-full">
                        <TabsList
                            data-testid="waitlist-tabs"
                            className="bg-cream-deep border border-border rounded-full p-1.5 h-auto inline-flex"
                        >
                            <TabsTrigger
                                value="homeowner"
                                data-testid="tab-homeowner"
                                className="rounded-full px-6 py-2.5 text-sm font-semibold data-[state=active]:bg-ink data-[state=active]:text-cream data-[state=active]:shadow-none text-ink-soft"
                            >
                                {t("waitlist.tabs.homeowner")}
                            </TabsTrigger>
                            <TabsTrigger
                                value="provider"
                                data-testid="tab-provider"
                                className="rounded-full px-6 py-2.5 text-sm font-semibold data-[state=active]:bg-ink data-[state=active]:text-cream data-[state=active]:shadow-none text-ink-soft"
                            >
                                {t("waitlist.tabs.provider")}
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="homeowner" className="mt-10">
                            <div className="grid lg:grid-cols-12 gap-10">
                                <div className="lg:col-span-4">
                                    <h2 className="font-heading text-2xl text-ink">{t("waitlist.homeowner.title")}</h2>
                                    <p className="mt-3 text-ink-soft leading-relaxed">
                                        {t("waitlist.homeowner.body")}
                                    </p>
                                </div>
                                <div id="homeowner" className="lg:col-span-8 bg-cream-deep border border-border rounded-3xl p-4 lg:p-6">
                                    <AirtableFormEmbed
                                        src={FORMS.homeownerCommunity}
                                        title={t("waitlist.homeowner.title")}
                                        testId="waitlist-homeowner-form"
                                        height={800}
                                    />
                                </div>
                            </div>
                        </TabsContent>

                        <TabsContent value="provider" className="mt-10">
                            <div className="grid lg:grid-cols-12 gap-10">
                                <div className="lg:col-span-4">
                                    <h2 className="font-heading text-2xl text-ink">{t("waitlist.provider.title")}</h2>
                                    <p className="mt-3 text-ink-soft leading-relaxed">
                                        {t("waitlist.provider.body")}
                                    </p>
                                </div>
                                <div id="provider" className="lg:col-span-8 bg-cream-deep border border-border rounded-3xl p-4 lg:p-6">
                                    <AirtableFormEmbed
                                        src={FORMS.foundingProvider}
                                        title={t("waitlist.provider.title")}
                                        testId="waitlist-provider-form"
                                        height={800}
                                    />
                                </div>
                            </div>
                        </TabsContent>
                    </Tabs>
                </div>
            </section>
        </div>
    );
}
